<style scoped>

  #consolidated-quotations-segment {
    min-height: 510px !important;
  }

  .chart-container {
    height: 400px !important;
  }

  .chart-info-label {
    margin-top: 85px !important;
  }

  #info-label {
    margin-top: 105px !important;
  }

</style>

<template>
  
  <div id="consolidated-quotations-segment" class="ui center aligned segment" :class="{ loading }">
    <div class="ui grid">
      <div class="eight wide column">
        <div class="ui basic left aligned segment no-margin no-padding">
          <h4 class="ui header no-margin no-padding">Consolidado</h4>
          <div class="grey text" style="font-size: 12px;">
            {{ lastUpdateMessage }}
          </div>
        </div>
      </div>
      <div class="eight wide column">
        <div class="ui basic right aligned segment no-margin no-padding">
          <i class="refresh icon clickable" @click="updateCharts({ force: true })"></i>
        </div>
      </div>
    </div>
    <div class="ui secondary tiny menu m-b-20">
      <a class="item" :class="{ active: (consolidatedOption == 'quotations') }" @click="consolidatedOption = 'quotations'">
        Orçamentos confirmados
      </a>
      <a class="item" :class="{ active: (consolidatedOption == 'billing') }" @click="consolidatedOption = 'billing'">
        Faturamento
      </a>
    </div>
    <div v-show="!error" class="ui grid">
      <div v-show="consolidatedOption == 'quotations'" class="chart-container sixteen wide column animated fadeIn">
        <canvas v-show="confirmedMonths.length > 0" id="confirmed-quotations-chart"></canvas>
        <h3 v-show="!loading && confirmedMonths.length == 0" class="chart-info-label ui center aligned icon grey text header">
          <i class="info circle icon"></i>
          Nenhum orçamento confirmado nos últimos meses
        </h3>
      </div>
      <div v-show="consolidatedOption == 'billing'" class="chart-container sixteen wide column animated fadeIn">
        <canvas v-show="billingMonths.length > 0" id="billing-chart"></canvas>
        <h3 v-show="!loading && billingMonths.length == 0" class="chart-info-label ui center aligned icon grey text header">
          <i class="info circle icon"></i>
          Nenhum faturamento nos últimos meses
        </h3>
      </div>
    </div>  
    <h3 id="info-label" v-if="!loading && error" class="ui center aligned icon grey text header">
      <i class="exclamation circle red icon animated flash"></i>
      Falha de comunicação
    </h3>  
  </div>

</template>

<script>

  import DashboardService from "@/services/dashboard-service"
  import { moment, numeral, colors } from "@/utils"

  const CHARTS_LABEL_FONT = {
   fontSize: 12,
   fontColor: "#1B1C1D",
   fontFamily: "Lato,'Helvetica Neue',Arial,Helvetica,sans-serif",
  }

  export default {
   
    data() {
      return {
        consolidatedOption: "quotations",
        loading: true,
        error: false,
        confirmedChart: null,
        billingChart: null,
        confirmedMonths: [],
        billingMonths: [],
        lastUpdateMessage: "",
        setIntervalId: null,
      }
    },

    mounted() {

      this.setIntervalId = setInterval(this.updateLastUpdateMessage, 60000)

      this.updateCharts()      
    },

    beforeDestroy() {
      clearInterval(this.setIntervalId)
    },

    methods: {

      async updateCharts(data) {

        let forceUpdate = (data && data.force)

        this.loading = true
        this.error = false

        try {          

          if (!forceUpdate && this.$store.state.consolidatedQuotationsChartData) {

            this.confirmedMonths = this.$store.state.consolidatedQuotationsChartData.confirmedMonths
            this.billingMonths = this.$store.state.consolidatedQuotationsChartData.billingMonths

          } else {

            this.confirmedMonths = await DashboardService.getConfirmedQuotations()
            this.billingMonths = await DashboardService.getBillingValue()

            this.$store.commit("setConsolidatedQuotationsChartData", { 
              confirmedMonths: this.confirmedMonths,
               billingMonths: this.billingMonths,
              lastUpdate: new Date() 
            })            
          }

          this.updateConfirmedChart()
          this.updateBillingChart()

          this.loading = false

          this.updateLastUpdateMessage()
          
        } catch (error) {

          this.loading = false
          this.error = true
          this.confirmedMonths = []
          this.billingMonths = []

          this.updateLastUpdateMessage()
          
          this.$errorHandler(error, { silent: true })
        }
      },

      updateConfirmedChart() {

        if (!this.confirmedChart)
          this.confirmedChart = new Chart($("#confirmed-quotations-chart"), { type: "bar"} )

        let labels = []

        let quantityDataset = { 
          label: "Quantidade", 
          backgroundColor: Chart.helpers.color(colors.blue).alpha(0.5).rgbString(),
          borderColor: colors.blue, 
          borderWidth: 1,
          data: [],
          yAxisID: "y-axis-quantity", 
        }
        
        let valueDataset = { 
          label: "Valor", 
          backgroundColor: Chart.helpers.color(colors.green).alpha(0.5).rgbString(),
          borderColor: colors.green, 
          borderWidth: 1,
          data: [],
          yAxisID: "y-axis-value", 
        }

        this.confirmedMonths.forEach((month) => {
          labels.push(month.label)
          quantityDataset.data.push(month.quantity)
          valueDataset.data.push(month.value)
        })

        this.confirmedChart.data = {
          labels,
          datasets: [quantityDataset, valueDataset],
        }

        this.confirmedChart.options = {     
          responsive: true,
          maintainAspectRatio: false,          
          legend: { display: true, position: "bottom", labels: CHARTS_LABEL_FONT },
          scales: {            
            yAxes: [
              { id: "y-axis-quantity", ticks: { beginAtZero: true, ...CHARTS_LABEL_FONT } }, 
              { id: "y-axis-value", ticks: { beginAtZero: true, ...CHARTS_LABEL_FONT, callback: (value) => numeral(value).format("(0.00 a)") } }
            ],
            xAxes: [{ ticks: { ...CHARTS_LABEL_FONT } }],
          },
          tooltips: {
            callbacks: {
              label: (item, data) => {
                if (item.datasetIndex == 0) {
                  return " Quantidade: " + this.$formatInteger(item.yLabel)
                } else {
                  return " R$ " + this.$formatCurrency(item.yLabel)
                }
              },
            },
          },
        }

        this.confirmedChart.update()
      },

      updateBillingChart() {

        if (!this.billingChart)
          this.billingChart = new Chart($("#billing-chart"), { type: "line"} )

        let labels = []

        let valueDataset = { 
          label: "Valor", 
          backgroundColor: colors.red,
          borderColor: colors.red,
          fill: false, 
          data: [],
        }

        this.billingMonths.forEach((month) => {
          labels.push(month.label)
          valueDataset.data.push(month.value)
        })

        this.billingChart.data = {
          labels,
          datasets: [valueDataset],
        }

        this.billingChart.options = {   
          responsive: true,
          maintainAspectRatio: false,             
          legend: { display: false },
          scales: {            
            yAxes: [{ ticks: { ...CHARTS_LABEL_FONT, callback: (value) => numeral(value).format("(0.00 a)") } }],
            xAxes: [{ ticks: { ...CHARTS_LABEL_FONT } }],
          },
          tooltips: {
            callbacks: {
              label: (item, data) => " R$ " + this.$formatCurrency(item.yLabel),
            },
          },
        }

        this.billingChart.update()
      },

      updateLastUpdateMessage() {

        if (this.$store.state.consolidatedQuotationsChartData) {          
          this.lastUpdateMessage = "Atualizado " + moment(this.$store.state.consolidatedQuotationsChartData.lastUpdate).fromNow() 
        } else {
          this.lastUpdateMessage = ""
        }   
      },
    },
  }
</script>

